import React, { useMemo, startTransition, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { getNoVehicleImageToDisplay } from 'site-modules/shared/utils/get-no-vehicle-image-to-display';
import { SOURCES } from 'client/utils/image-helpers';
import { EDM_LEASE_DEALS_VEHICLE_CARD_CREATIVE_ID } from 'site-modules/lease-deals/constants/lease-deals-constants';
import { Image } from 'site-modules/shared/components/image/image';
import { LeaseDealsCardDescription } from 'site-modules/lease-deals/components/lease-deals-card-description/lease-deals-card-description';
import { LeaseDealsDetailsModal } from 'site-modules/lease-deals/components/lease-deals-details-modal/lease-deals-details-modal';
import { LeaseDealsValuePill } from 'site-modules/lease-deals/components/lease-deals-value-pill/lease-deals-value-pill';
import {
  createDate,
  getCurrentDate,
  getDifferenceInDays,
  isSameDay,
} from 'client/site-modules/shared/utils/date-utils';
import { formatPriceStringV2 } from 'client/site-modules/shared/utils/price-utils';

import './lease-deals-card.scss';

export const LeaseDealsCard = ({
  photo = undefined,
  bodyType = undefined,
  paymentMedian = undefined,
  payment,
  isLazyImage = false,
  msrp,
  term,
  dueAtSigning,
  endDate,
  year,
  make,
  model,
  styleId,
  isGemDeal,
  effectivePaymentRatio,
  effectivePayment,
  effectivePaymentMedian = undefined,
  isShop4931ZeroDownRoute = false,
  electricityRange = undefined,
  combinedMpg = undefined,
  edmundsRating = undefined,
  consumerRating = undefined,
  ...rest
}) => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const toggleModal = useCallback(() => {
    startTransition(() => setIsOpenModal(prev => !prev));
  }, []);
  const errorImage = getNoVehicleImageToDisplay(bodyType);

  const daysRemaining = useMemo(() => {
    const now = getCurrentDate();
    const end = createDate(endDate);

    if (isSameDay(now, end)) {
      return 0;
    }

    return getDifferenceInDays(end);
  }, [endDate]);

  const daysRemainingText = useMemo(() => {
    if (daysRemaining >= 2) {
      return `${daysRemaining} days left`;
    } else if (daysRemaining === 1) {
      return `${daysRemaining} day left`;
    }
    return 'Last day';
  }, [daysRemaining]);

  return (
    <div
      className="pos-r lease-deals-card bg-white d-flex flex-column"
      data-tracking-parent={EDM_LEASE_DEALS_VEHICLE_CARD_CREATIVE_ID}
    >
      <button
        onClick={() => {
          toggleModal();
        }}
        data-tracking-value={styleId}
        data-tracking-id="view_full_description"
        className="pos-a top-0 left-0 w-100 h-100 border-0 bg-transparent"
        aria-label={`See more details on ${year} ${make} ${model} ${formatPriceStringV2(payment)}/mo deal`}
      />
      <div className="image-wrapper p-0 pos-r flex-shrink-0">
        <Image
          lazy={isLazyImage}
          alt={photo?.title}
          source={SOURCES.MEDIA}
          errorImage={errorImage}
          imageId={photo?.href ?? ''}
          className="vehicle-image w-100 h-100 object-fit-cover"
        />
        <div className="pos-a top-0 m-1 d-flex align-items-center gap-0_5">
          {isGemDeal && (
            <div className="gem-deal p-0_75 d-flex align-items-center">
              <Image
                lazy
                ariaHidden
                source={SOURCES.STATIC}
                imageId="/icons/lease-deals-gem.svg"
                className="object-fit-cover"
              />
              <span className="size-14">
                <span className="text-cool-gray-10 fw-medium">Gem deal</span>
              </span>
            </div>
          )}
          {daysRemaining >= 0 && daysRemaining <= 7 && (
            <div className="days-remaining p-0_75 d-flex align-items-center">
              <Image
                lazy
                ariaHidden
                source={SOURCES.STATIC}
                imageId="/icons/alarm-clock.svg"
                className="object-fit-cover"
              />
              <span className="size-14 fw-medium">{daysRemainingText}</span>
            </div>
          )}
        </div>
      </div>
      <div className="lease-deals-card-description-wrapper w-100 px-1_5 pb-1_5 flex-grow-1">
        <LeaseDealsValuePill
          term={term}
          payment={payment}
          dueAtSigning={dueAtSigning}
          effectivePaymentRatio={effectivePaymentRatio}
        />
        <div className="transition-inner h-100">
          <div className="w-100 h-100">
            <LeaseDealsCardDescription
              photo={photo}
              payment={payment}
              errorImage={errorImage}
              msrp={msrp}
              dueAtSigning={dueAtSigning}
              term={term}
              bodyType={bodyType}
              endDate={endDate}
              year={year}
              make={make}
              model={model}
              styleId={styleId}
              effectivePayment={effectivePayment}
              effectivePaymentMedian={effectivePaymentMedian}
              paymentMedian={paymentMedian}
              isShop4931ZeroDownRoute={isShop4931ZeroDownRoute}
              {...rest}
            />
          </div>
          <LeaseDealsDetailsModal
            {...rest}
            photo={photo}
            payment={payment}
            bodyType={bodyType}
            isOpen={isOpenModal}
            onClose={toggleModal}
            msrp={msrp}
            dueAtSigning={dueAtSigning}
            term={term}
            endDate={endDate}
            year={year}
            make={make}
            model={model}
            styleId={styleId}
            isGemDeal={isGemDeal}
            effectivePaymentRatio={effectivePaymentRatio}
            effectivePayment={effectivePayment}
            effectivePaymentMedian={effectivePaymentMedian}
            isShop4931ZeroDownRoute={isShop4931ZeroDownRoute}
            electricityRange={electricityRange}
            edmundsRating={edmundsRating}
            consumerRating={consumerRating}
            combinedMpg={combinedMpg}
            daysRemaining={daysRemaining}
          />
        </div>
      </div>
    </div>
  );
};

LeaseDealsCard.propTypes = {
  photo: PropTypes.shape({ title: PropTypes.string, href: PropTypes.string }),
  payment: PropTypes.number.isRequired,
  bodyType: PropTypes.string,
  paymentMedian: PropTypes.number,
  isLazyImage: PropTypes.bool,
  endDate: PropTypes.string.isRequired,
  msrp: PropTypes.number.isRequired,
  term: PropTypes.number.isRequired,
  dueAtSigning: PropTypes.number.isRequired,
  year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  make: PropTypes.string.isRequired,
  model: PropTypes.string.isRequired,
  styleId: PropTypes.number.isRequired,
  effectivePaymentRatio: PropTypes.number.isRequired,
  effectivePayment: PropTypes.number.isRequired,
  effectivePaymentMedian: PropTypes.number,
  isShop4931ZeroDownRoute: PropTypes.bool,
  electricityRange: PropTypes.number,
  combinedMpg: PropTypes.number,
  edmundsRating: PropTypes.number,
  consumerRating: PropTypes.number,
};
